import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Spinner,
  Textarea,
  Input,
  Field,
} from '@fluentui/react-components';
import { ArrowLeftRegular, SaveRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown } from '../../../components/common';
import { ApiClient, getErrorMessage, newToast } from '../../../configurations/index.config';
import type { Site, Employee } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #edebe9',
  },
});

const INCIDENT_TYPES = [
  { id: 'Theft', label: 'Theft' },
  { id: 'Vandalism', label: 'Vandalism' },
  { id: 'Trespassing', label: 'Trespassing' },
  { id: 'Fire', label: 'Fire' },
  { id: 'Medical', label: 'Medical Emergency' },
  { id: 'Assault', label: 'Assault' },
  { id: 'Suspicious Activity', label: 'Suspicious Activity' },
  { id: 'Property Damage', label: 'Property Damage' },
  { id: 'Unauthorized Access', label: 'Unauthorized Access' },
  { id: 'Other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'critical', label: 'Critical' },
];

const CreateIncidentFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [siteId, setSiteId] = useState<string>('');
  const [employeeId, setEmployeeId] = useState<string>('');
  const [incidentType, setIncidentType] = useState<string>('');
  const [severity, setSeverity] = useState<string>('medium');
  const [incidentDate, setIncidentDate] = useState<string>('');
  const [incidentTime, setIncidentTime] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [actionsTaken, setActionsTaken] = useState<string>('');
  const [gpsLat, setGpsLat] = useState<string>('');
  const [gpsLng, setGpsLng] = useState<string>('');

  useEffect(() => {
    fetchData();
    setDefaultDateTime();
  }, []);

  const setDefaultDateTime = () => {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0] || '';
    const timeStr = now.toTimeString().slice(0, 5);
    setIncidentDate(dateStr);
    setIncidentTime(timeStr);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sitesRes, employeesRes] = await Promise.all([
        ApiClient.get('/get-sites'),
        ApiClient.get('/get-employee-data'),
      ]);

      if (sitesRes.data.status === 'success') {
        setSites(sitesRes.data.data || []);
      }
      if (employeesRes.data.status === 'success') {
        setEmployees(employeesRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!siteId) {
      newToast('error', 'Please select a site');
      return;
    }
    if (!employeeId) {
      newToast('error', 'Please select the reporting employee');
      return;
    }
    if (!incidentType) {
      newToast('error', 'Please select an incident type');
      return;
    }
    if (!description.trim()) {
      newToast('error', 'Please provide a description');
      return;
    }

    setSaving(true);
    try {
      const incidentTimestamp =
        incidentDate && incidentTime
          ? `${incidentDate}T${incidentTime}:00`
          : new Date().toISOString();

      const payload: any = {
        employee_id: parseInt(employeeId),
        site_id: parseInt(siteId),
        incident_timestamp: incidentTimestamp,
        incident_type: incidentType,
        severity: severity,
        description: description.trim(),
      };

      if (actionsTaken.trim()) {
        payload.actions_taken = actionsTaken.trim();
      }
      if (gpsLat && gpsLng) {
        payload.gps_lat = parseFloat(gpsLat);
        payload.gps_lng = parseFloat(gpsLng);
      }

      const response = await ApiClient.post('/create-incident-report', payload);

      if (response.data.status === 'success') {
        newToast('success', 'Incident report created successfully');
        navigate('/account/incident-reports');
      } else {
        newToast('error', response.data.message || 'Failed to create incident report');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading..." />
      </div>
    );
  }

  return (
    <PermissionGate privilege="incidents.create" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/incident-reports')}
          />
          <Text className={styles.pageTitle}>Create Incident Report</Text>
        </div>

        <ContentCard>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '24px' }}>
            Incident Information
          </Text>

          <div className={styles.formGrid}>
            <Field label="Site *" required>
              <SearchableDropdown
                placeholder="Select site"
                selectedValue={siteId}
                onSelect={value => setSiteId(value)}
                options={sites.map(site => ({
                  id: site.id.toString(),
                  label: site.name,
                }))}
              />
            </Field>

            <Field label="Reported By *" required>
              <SearchableDropdown
                placeholder="Select employee"
                selectedValue={employeeId}
                onSelect={value => setEmployeeId(value)}
                options={employees.map(emp => ({
                  id: emp.id.toString(),
                  label: `${emp.first_name} ${emp.surname}`,
                }))}
              />
            </Field>

            <Field label="Incident Type *" required>
              <SearchableDropdown
                placeholder="Select incident type"
                selectedValue={incidentType}
                onSelect={value => setIncidentType(value)}
                options={INCIDENT_TYPES}
              />
            </Field>

            <Field label="Severity *" required>
              <SearchableDropdown
                placeholder="Select severity"
                selectedValue={severity}
                onSelect={value => setSeverity(value)}
                options={SEVERITY_OPTIONS}
              />
            </Field>

            <Field label="Incident Date">
              <Input
                type="date"
                value={incidentDate}
                onChange={e => setIncidentDate(e.target.value)}
              />
            </Field>

            <Field label="Incident Time">
              <Input
                type="time"
                value={incidentTime}
                onChange={e => setIncidentTime(e.target.value)}
              />
            </Field>

            <Field label="Description *" required className={styles.fullWidth}>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Provide a detailed description of the incident..."
                rows={4}
              />
            </Field>

            <Field label="Actions Taken" className={styles.fullWidth}>
              <Textarea
                value={actionsTaken}
                onChange={e => setActionsTaken(e.target.value)}
                placeholder="Describe any actions taken in response to the incident..."
                rows={3}
              />
            </Field>

            <Field label="GPS Latitude">
              <Input
                type="number"
                step="any"
                value={gpsLat}
                onChange={e => setGpsLat(e.target.value)}
                placeholder="e.g., 5.6038"
              />
            </Field>

            <Field label="GPS Longitude">
              <Input
                type="number"
                step="any"
                value={gpsLng}
                onChange={e => setGpsLng(e.target.value)}
                placeholder="e.g., -0.1872"
              />
            </Field>
          </div>

          <div className={styles.actions}>
            <Button appearance="secondary" onClick={() => navigate('/account/incident-reports')}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<SaveRegular />}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Incident'}
            </Button>
          </div>
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default CreateIncidentFluentUI;
